import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { TrashIcon, PencilIcon, PhoneIcon, CalendarIcon, ChevronRightIcon, ChevronDownIcon, EnvelopeIcon, ArchiveBoxIcon, ClipboardDocumentIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { updateAnnonce, deleteAnnonce } from 'src/service/annonceApi';
import { useNotification } from 'src/contexts/NotificationContext';
import { getConversation } from 'src/service/conversationApi';
import { countUnreadMessages } from 'src/utils/messageUtils';
import Label from '../../../components/Label';
import { ProductImgStyle } from 'src/service/componentService';
import { notifySuccess } from 'src/service/notificationService';
import { Badge } from '@mui/material';

export default function ShopProductCard({ ads, handleClickOpenDialog, fetchFoundAds }) {
  const { id, title, filePath, status, annonceAttributes = [], category, stockage, date, contact, userExpedition } = ads || {};
  const { triggerRefresh } = useNotification();
  const frenchdate = date ? new Date(date) : new Date();
  const [secretOpen, setSecretOpen] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [conversationCount, setConversationCount] = useState(0);

  const annonceDate = frenchdate.toLocaleString('fr-FR', { month: 'long', day: 'numeric', year: 'numeric' });
  const boxName = stockage?.boxName || 'pas de stockage';
  const cover = filePath
    ? `${process.env.REACT_APP_PUBLIC_URL}/images/annonces/${filePath}`
    : category?.defaultImage
    ? `${process.env.REACT_APP_PUBLIC_URL}/images/annonces/default/${category.defaultImage}`
    : `${process.env.REACT_APP_PUBLIC_URL}/images/annonces/default/default.jpg`;

  const [actionSentence, setActionSentence] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [action, setAction] = useState('');
  const [handleOnAction, setHandleOnAction] = useState('');
  const [openDialog, setOpenDialog] = useState(false);

  const blocAdress =
    userExpedition && userExpedition.firstname ? (
      <div className="space-y-1">
        <p className="text-sm text-gray-600">
          {userExpedition.firstname} {userExpedition.lastname}
        </p>
        <p className="text-sm text-gray-600">{userExpedition.street}</p>
        <p className="text-sm text-gray-600">{userExpedition.lieudit}</p>
        <p className="text-sm text-gray-600">{userExpedition.batiment}</p>
        <p className="text-sm text-gray-600">{userExpedition.apptEtage}</p>
        <p className="text-sm text-gray-600">
          {userExpedition.cp} {userExpedition.ville}
        </p>
        <p className="text-sm text-gray-600">{userExpedition.country}</p>
      </div>
    ) : (
      ''
    );

  const handleClickOpen = (action) => {
    switch (action) {
      case 'Supprimer':
        setActionSentence(`Voulez vous supprimer cet Objet : ${category?.name || 'Sans catégorie'} ${id}`);
        setAction('Supprimer');
        setHandleOnAction('delete');
        setOpenDialog(true);
        break;

      case 'send':
        setActionSentence(`Voulez vous déclarer cet objet comme envoyé ? : ${category?.name || 'Sans catégorie'} ${id}`);
        setAction('Oui Il a été envoyé');
        setHandleOnAction('send');
        setOpenDialog(true);
        break;

      default:
        return;
    }
  };

  const handleAction = () => {
    switch (handleOnAction) {
      case 'delete':
        handleDelete();
        setOpenDialog(false);
        break;
      case 'send': {
        handleIsSend();
        setOpenDialog(false);
      }
      default:
        return;
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = async () => {
    try {
      await deleteAnnonce(id);
      setHandleOnAction('');
      setAction('');
      setActionSentence('');
      setActionMessage('');
      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  const handleIsSend = async () => {
    const data = {
      status: 'ENVOYÉ',
    };
    try {
      await updateAnnonce(id, data);
      setHandleOnAction('');
      setAction('');
      setActionSentence('');
      setActionMessage('');
      
      // Déclencher le rafraîchissement des notifications
      triggerRefresh();
      
      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      notifySuccess(`ID ${text} copié dans le presse-papiers !`);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };



  useEffect(() => {
    let isMounted = true;
    
    const checkUnreadMessages = async () => {
      try {
        const conversations = await getConversation(`?annonce=${id}`);
        if (isMounted) {
          const totalUnread = countUnreadMessages(conversations);
          setUnreadMessages(totalUnread);
          setConversationCount(conversations.length);
  
        }
      } catch (error) {
        console.error('Erreur lors de la vérification des messages non lus:', error);
      }
    };

    checkUnreadMessages();
    const interval = setInterval(checkUnreadMessages, 60000); // Vérifie toutes les 60 secondes

    // Ajout : écouteur pour mise à jour immédiate
    const handleMessageRead = () => checkUnreadMessages();
    window.addEventListener('messageRead', handleMessageRead);

    return () => {
      isMounted = false;
      clearInterval(interval);
      window.removeEventListener('messageRead', handleMessageRead);
    };
  }, [id]);



  const secretAttributes = annonceAttributes.filter(attr => attr.isSecret);
  const publicAttributes = annonceAttributes.filter(attr => !attr.isSecret);

  return (
    <div 
      className="bg-white rounded-lg shadow-md overflow-hidden min-h-[450px] flex flex-col relative cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02] group"
      onClick={() => handleClickOpenDialog(ads)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClickOpenDialog(ads);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Voir les détails de l'objet ${title}`}
    >
      {/* Badge statut en haut à gauche */}
      <span className={`absolute top-2 left-2 px-3 py-1 rounded-full shadow-md z-10 text-xs font-bold flex items-center space-x-1 ${
        status === 'TROUVÉ' ? 'bg-green-600 text-white' : 
        status === 'PERDU' ? 'bg-yellow-500 text-white' :
        status === 'RESTITUÉ' ? 'bg-blue-600 text-white' :
        status === 'ENVOYÉ' ? 'bg-purple-600 text-white' :
        status === 'A_EXPEDIER' ? 'bg-orange-500 text-white' :
        status === 'PAYÉ' ? 'bg-emerald-600 text-white' :
        'bg-gray-500 text-white'
      }`}>
        <span className="w-2 h-2 rounded-full bg-white/30"></span>
        <span>{status}</span>
      </span>

      {/* Badge messages non lus en haut à droite */}
      {unreadMessages > 0 && (
        <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md z-10 flex items-center space-x-1 animate-pulse">
          <span className="w-2 h-2 rounded-full bg-white/30"></span>
          <span>{unreadMessages} message{unreadMessages > 1 ? 's' : ''} non lu{unreadMessages > 1 ? 's' : ''}</span>
        </span>
      )}



      <div className="relative h-48">
        <img
          src={cover}
          alt={title}
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
              <ArchiveBoxIcon className="w-5 h-5 text-gray-600" />
            </div>
            <span className="text-white text-sm font-medium">{boxName}</span>
          </div>
        </div>
      </div>

      <div className="p-4 flex-grow flex flex-col">
        <div className="flex items-center justify-between px-4 py-2">
          {/* Picto catégorie à gauche de la date, puis l'id */}
          <div className="flex items-center">
            {category?.image && (
              <img src={`/pictos/${category.image}.svg`} alt={category.name} className="w-7 h-7 mr-2" />
            )}
            <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-700 font-bold ml-1">{id}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(id.toString());
                }}
                className="p-1 text-gray-400 hover:text-[#1b9faa] hover:bg-[#1b9faa]/10 rounded transition-all duration-200"
                title="Copier l'ID"
              >
                <ClipboardDocumentIcon className="w-3 h-3" />
              </button>
            </div>
          </div>
          <span className="text-xs text-gray-500 ml-auto flex items-center">
            <CalendarIcon className="w-4 h-4 mr-1" />
            {annonceDate}
          </span>
        </div>

        <div className="px-4 py-2">
          <h3 className="text-lg font-semibold text-gray-800 mb-1 group-hover:text-[#1b9faa] transition-colors duration-200">{title}</h3>
        </div>

        <div className="space-y-2 flex-grow">
          {publicAttributes.map((attribute, index) => (
            <div key={index} className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">{attribute.attribute}:</span>
              {attribute.uiType === 'colorcheckbox' ? (
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-4 h-4 rounded-full border border-gray-200" 
                    style={{ backgroundColor: attribute.uiValue }}
                  />
                  <span className="text-sm font-medium text-gray-900">{attribute.attributeValue}</span>
                </div>
              ) : (
                <span className="text-sm font-medium text-gray-900">{attribute.attributeValue}</span>
              )}
            </div>
          ))}

          {secretAttributes.length > 0 && (
            <div className="mt-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSecretOpen(!secretOpen);
                }}
                className="flex items-center text-sm text-[#1b9faa] hover:text-[#15808a] transition-colors duration-200"
              >
                {secretOpen ? (
                  <ChevronDownIcon className="w-4 h-4 mr-1" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 mr-1" />
                )}
                Détails secrets ({secretAttributes.length})
              </button>

              {secretOpen && (
                <div className="mt-2 space-y-2 pl-4">
                  {secretAttributes.map((attribute, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">{attribute.attribute}:</span>
                      {attribute.uiType === 'colorcheckbox' ? (
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-4 h-4 rounded-full border border-gray-200" 
                            style={{ backgroundColor: attribute.uiValue }}
                          />
                          <span className="text-sm font-medium text-gray-900">{attribute.attributeValue}</span>
                        </div>
                      ) : (
                        <span className="text-sm font-medium text-gray-900">{attribute.attributeValue}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClickOpenDialog(ads);
              }}
              className="flex items-center justify-center w-12 h-12 bg-blue-100 hover:bg-blue-200 rounded-full transition-all duration-200 group/edit"
              title="Voir la messagerie"
            >
              <Badge badgeContent={conversationCount > 0 ? conversationCount : null} color="primary">
                <ChatBubbleLeftRightIcon className="w-6 h-6 text-blue-600 group-hover/edit:scale-110 transition-transform duration-200" />
              </Badge>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowActions(!showActions);
              }}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-all duration-200"
              title="Actions"
            >
              <ChevronDownIcon className={`w-5 h-5 transition-transform duration-200 ${showActions ? 'rotate-180' : ''}`} />
            </button>
          </div>
          
          {/* Indicateur visuel que la carte est cliquable */}
          <div className="flex items-center text-xs text-gray-400 group-hover:text-[#1b9faa] transition-colors duration-200">
            <span className="mr-1">Cliquer pour voir les détails</span>
            <ChevronRightIcon className="w-3 h-3" />
          </div>
        </div>

        {showActions && (
          <div className="mt-2 p-2 bg-gray-50 rounded-md animate-in slide-in-from-top-2 duration-200">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClickOpen('Supprimer');
              }}
              className="flex items-center text-sm text-red-600 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-all duration-200"
            >
              <TrashIcon className="w-4 h-4 mr-1" />
              Supprimer
            </button>
          </div>
        )}
      </div>

      {openDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-in fade-in duration-200">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 animate-in slide-in-from-bottom-4 duration-200">
            <h3 className="text-lg font-semibold mb-4">{actionSentence}</h3>
            <div className="flex justify-end space-x-4">
              <button
                onClick={handleCloseDialog}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors duration-200"
              >
                Annuler
              </button>
              <button
                onClick={handleAction}
                className="px-4 py-2 bg-[#1b9faa] text-white rounded-md hover:bg-[#15808a] transition-colors duration-200"
              >
                {action}
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}
